import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../../../../../components/RenderWithTooltip";
import StatusChip from "../../../../../../../../components/StatusChip";
import Text from "../../../../../../../../components/Text";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useArchiveCareplan } from "../../../../../../../../queries/care-plan/use-patient-careplan-archive";
import { usePatientCarePlanList } from "../../../../../../../../queries/care-plan/use-patient-careplan-list";
import { CarePlan } from "../../../../../../../../services";
import {
  NonNullableDate,
  formatDateTime,
} from "../../../../../../../../utils/date-time-utility";
import {
  DEFAULT_VALUE_LABEL_LIST,
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getXtenantId,
  roleFilter,
  transformMultiSelectFrontEndVal,
} from "../../../../../../../../utils/utils";
import UpsertCarePlanDrawer from "../../care-plan/UpsertCarePlanDrawer";

interface CarePlanListProps {
  isInsideVisitNote?: boolean;
}

const CarePlanList = ({ isInsideVisitNote = false }: CarePlanListProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams() as { patientId: any };
  const { mutateAsync: archiveCareplan } = useArchiveCareplan();
  const careplanDisclosure = useDisclosure(false);
  const { hasReadOnly, hasUpdate, hasDelete } = useRoleController("Clinical");
  const context = useOutletContext() as { showArchived: boolean };
  const showArchived = context?.showArchived ?? false;

  // const showArchivedCarePlans =
  //   useQueryParams("showArchivedCarePlans") === "true";
  const { data: careplanList, refetch } = usePatientCarePlanList({
    patientUuid: patientId,
    pagination: pagination,
    gridSorting: gridSorting,
    current: undefined,
    archive: showArchived,
    xTENANTID: getXtenantId() ?? undefined,
  });

  const ACTION_MENU_OPTIONS = [
    { value: "edit", label: "Edit" },
    {
      value: showArchived ? "unarchive" : "archive",
      label: showArchived ? "Unarchive" : "Archive",
    },
  ];

  const deleteCareplanDisclosure = useDeleteDisClosure();
  const COLUMNS = applyDefaultColumnSettings<CarePlan>(
    [
      // {
      //   field: "srNo",
      //   headerName: "Sr. No",
      //   flex: 0.15,
      //   minWidth: 50,
      //   renderCell: (params) =>
      //     params.api.getAllRowIds().indexOf(params.id) + 1,
      // },
      {
        field: "title",
        headerName: "Title",
        flex: 0.35,
        minWidth: 100,
        renderCell: ({ row, value }) => (
          <Text
            isTabletext
            c="primary"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              careplanDisclosure.open({
                ...row,
                icdCodes: transformMultiSelectFrontEndVal(
                  DEFAULT_VALUE_LABEL_LIST,
                  row?.icdCodes
                ),
              });
            }}
          >
            <RenderWithTooltip value={value} />
          </Text>
        ),
      },
      {
        field: "start_date",
        headerName: "Start Date",
        flex: 0.25,
        minWidth: 90,
        renderCell: ({ row }) => NonNullableDate(row?.startDate as any),
      },
      {
        field: "end_date",
        headerName: "End Date",
        flex: 0.25,
        minWidth: 80,
        renderCell: ({ row }) => NonNullableDate(row?.endDate as any),
      },
      {
        field: "assignDate",
        headerName: "Assigned Date & Time",
        flex: 0.4,
        minWidth: 140,
        renderCell: ({ row }) =>
          row?.assignDate ? formatDateTime(row?.assignDate) : "-",
      },
      {
        field: "assignedBy",
        headerName: "Assigned By",
        flex: 0.3,
        minWidth: 90,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "active",
        headerName: "Status",
        flex: 0.2,
        minWidth: 80,
        renderCell: ({ value }) => <StatusChip value={value} />,
      },
      {
        field: "deception",
        headerName: "Note",
        flex: 0.45,
        minWidth: 120,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: ACTION_MENU_OPTIONS,
                compWith: {
                  edit: hasUpdate,
                  delete: hasDelete,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit") {
                  const editedRow = {
                    ...row,
                    icdCodes: transformMultiSelectFrontEndVal(
                      DEFAULT_VALUE_LABEL_LIST,
                      row?.icdCodes
                    ),
                  };

                  careplanDisclosure.open(editedRow);
                } else deleteCareplanDisclosure.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["start_date", "end_date", "assignDate"]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveCareplan({
      carePlanId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <UpsertCarePlanDrawer
        disclosureHost={careplanDisclosure}
        curFormState="edit"
      />

      <DeleteModal
        w="380px"
        open={deleteCareplanDisclosure.opened}
        onClose={deleteCareplanDisclosure.close}
        id={deleteCareplanDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(!showArchived, id)}
        content={`You want to ${
          showArchived ? "unarchive" : "archive"
        } this Care Plan`}
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={careplanList?.data?.content ?? []}
        rowCount={careplanList?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, hasReadOnly || isInsideVisitNote)}
        // sx={{ marginTop: "40px" }}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        sortModel={gridSorting}
        onSortModelChange={setGridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default CarePlanList;
